import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Trash2, Mail, Search, CalendarClock } from 'lucide-react';
import { format } from 'date-fns';
import { EXPERIENCE_DATA, RIG_DATA } from '@/components/booking/bookingDefaults';

const STATUSES = ['waiting', 'notified', 'cancelled'];

const STATUS_STYLES = {
  waiting: 'bg-yellow-400/10 text-yellow-400',
  notified: 'bg-primary/10 text-primary',
  cancelled: 'bg-white/5 text-white/30',
};

function expName(key) { return EXPERIENCE_DATA[key]?.name || (key ? key.replace(/_/g, ' ').toUpperCase() : '—'); }
function rigName(key) { return RIG_DATA[key]?.name || (key ? key.replace(/_/g, ' ').toUpperCase() : '—'); }

export default function WaitlistAdmin() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['admin-waitlist'],
    queryFn: () => base44.entities.SlotWaitlist.list('-created_date', 500),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin-waitlist'] });

  const setStatus = async (id, status) => {
    await base44.entities.SlotWaitlist.update(id, { status });
    refresh();
  };
  const remove = async (id) => {
    if (!window.confirm('Remove this waitlist entry?')) return;
    await base44.entities.SlotWaitlist.delete(id);
    refresh();
  };

  const filtered = entries.filter((e) => {
    if (filter !== 'all' && e.status !== filter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [e.customer_name, e.customer_email, e.experience_type, e.rig_type, e.date, e.time_slot]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
  });

  const waitingCount = entries.filter((e) => e.status === 'waiting').length;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-white/40 text-xs">{filtered.length} entr{filtered.length === 1 ? 'y' : 'ies'} · <span className="text-primary">{waitingCount} waiting</span></p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customer, email, date…"
            className="w-full bg-[#111115] border border-white/10 text-white text-sm h-10 pl-9 pr-3 placeholder:text-white/20 focus:border-primary/60 focus:outline-none" />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)}
          className="bg-[#111115] border border-white/10 text-white text-sm font-bold h-10 px-3 focus:outline-none focus:border-primary/60">
          <option value="all" className="bg-[#0d0d0f]">ALL</option>
          {STATUSES.map((s) => <option key={s} value={s} className="bg-[#0d0d0f]">{s.toUpperCase()}</option>)}
        </select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-white/5 bg-[#111115]">
          <CalendarClock className="w-10 h-10 text-white/10 mx-auto mb-4" />
          <p className="text-white/30 text-sm">No waitlist entries</p>
          <p className="text-white/20 text-xs mt-1">When a sold-out slot is freed, the next person in line is emailed automatically.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtered.map((e) => (
            <div key={e.id} className="bg-[#111115] border border-white/5 p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="text-white font-bold text-sm">{e.customer_name || 'Customer'}</p>
                  <p className="text-white/30 text-xs mt-0.5">{expName(e.experience_type)} · {rigName(e.rig_type)}</p>
                  <p className="text-white/60 text-xs mt-1.5">
                    {e.date ? format(new Date(e.date), 'dd MMM yyyy') : '—'} · {e.time_slot || '—'}
                  </p>
                  <p className="text-white/40 text-xs mt-1 break-all">{e.customer_email}{e.customer_phone ? ` · ${e.customer_phone}` : ''}</p>
                  {e.notified_at && <p className="text-primary/60 text-[10px] mt-1">Notified {format(new Date(e.notified_at), 'dd MMM yyyy HH:mm')}</p>}
                </div>
                <span className={`text-[10px] font-bold tracking-widest px-2.5 py-1 shrink-0 ${STATUS_STYLES[e.status] || 'bg-white/5 text-white/40'}`}>
                  {e.status.toUpperCase()}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
                {e.status !== 'waiting' && (
                  <button onClick={() => setStatus(e.id, 'waiting')} className="text-[10px] font-bold tracking-widest border border-white/10 text-white/50 hover:text-white hover:border-white/25 px-2.5 py-1.5 transition-colors">RESET TO WAITING</button>
                )}
                {e.status !== 'notified' && (
                  <button onClick={() => setStatus(e.id, 'notified')} className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest border border-primary/20 text-primary hover:bg-primary/5 px-2.5 py-1.5 transition-colors">
                    <Mail className="w-3 h-3" /> MARK NOTIFIED
                  </button>
                )}
                <button onClick={() => remove(e.id)} className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest border border-red-500/20 text-red-400 hover:bg-red-500/5 px-2.5 py-1.5 transition-colors ml-auto">
                  <Trash2 className="w-3 h-3" /> DELETE
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}